import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiKeys as keysApi, developers as devApi, logs as logsApi } from '../api/resources.js';
import { Banner, Card, CopyButton, Empty, ErrorState, Loading } from '../components/ui.js';
import { useToast } from '../components/toast.js';
import { formatDateTime, formatRelative, statusCodeTone } from '../lib/format.js';
import { useAction, useAsync } from '../lib/hooks.js';

/** API Keys (PRD §14). The secret is displayed once, and the UI says so plainly. */
export function ApiKeysPage() {
  const toast = useToast();
  const [form, setForm] = useState<{ name: string; environment: 'live' | 'test' }>({
    name: '',
    environment: 'live',
  });
  const [revealed, setRevealed] = useState<{ name: string; secret: string } | null>(null);

  const keys = useAsync(() => keysApi.list(), []);

  const create = useAction(async () => {
    const created = await keysApi.create(form.name, form.environment);
    setRevealed({ name: created.name, secret: created.secret });
    setForm({ name: '', environment: 'live' });
    keys.reload();
  });

  const revoke = useAction(async (id: string) => {
    await keysApi.revoke(id);
    toast.success('Chave revogada', 'Ela para de funcionar imediatamente.');
    keys.reload();
  });

  return (
    <>
      {revealed && (
        <Card
          title={`Chave "${revealed.name}" criada`}
          description="Copie agora. Esta é a única vez que ela aparece."
          actions={
            <button type="button" className="btn btn-sm" onClick={() => setRevealed(null)}>
              Já guardei
            </button>
          }
        >
          <Banner tone="warn">
            A Talkium não armazena a chave completa — não existe endpoint capaz de revelá-la depois.
          </Banner>
          <div className="row" style={{ marginTop: 12 }}>
            <code className="mono">{revealed.secret}</code>
            <CopyButton value={revealed.secret} />
          </div>
        </Card>
      )}

      <Card title="Nova API Key">
        <form
          className="form-row"
          onSubmit={(event) => {
            event.preventDefault();
            void create.run();
          }}
        >
          <div>
            <label htmlFor="key-name">Nome</label>
            <input
              id="key-name"
              value={form.name}
              onChange={(event) => setForm((c) => ({ ...c, name: event.target.value }))}
              placeholder="Integração ERP"
              required
              minLength={2}
            />
          </div>
          <div>
            <label htmlFor="key-env">Ambiente</label>
            <select
              id="key-env"
              value={form.environment}
              onChange={(event) =>
                setForm((c) => ({ ...c, environment: event.target.value as 'live' | 'test' }))
              }
            >
              <option value="live">Produção (tk_live_)</option>
              <option value="test">Teste (tk_test_)</option>
            </select>
          </div>
          <button type="submit" className="btn btn-primary" disabled={create.pending}>
            {create.pending ? 'Criando…' : 'Criar chave'}
          </button>
        </form>
        {create.error != null && (
          <div style={{ marginTop: 12 }}>
            <ErrorState error={create.error} />
          </div>
        )}
      </Card>

      <Card title="Chaves" bodyless>
        {keys.loading ? (
          <Loading />
        ) : keys.error ? (
          <ErrorState error={keys.error} onRetry={keys.reload} />
        ) : keys.data?.data.length === 0 ? (
          <Empty title="Nenhuma chave criada" />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Prefixo</th>
                  <th>Ambiente</th>
                  <th>Último uso</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {(keys.data?.data ?? []).map((key) => (
                  <tr key={key.id}>
                    <td>{key.name}</td>
                    <td className="mono">{key.prefix}…</td>
                    <td>
                      <span
                        className={`pill pill-${key.environment === 'live' ? 'brand' : 'neutral'}`}
                      >
                        {key.environment}
                      </span>
                    </td>
                    <td className="subtle">{formatRelative(key.lastUsedAt)}</td>
                    <td>
                      <span className={`pill pill-${key.revokedAt ? 'danger' : 'ok'}`}>
                        {key.revokedAt ? 'revogada' : 'ativa'}
                      </span>
                    </td>
                    <td>
                      {!key.revokedAt && (
                        <button
                          type="button"
                          className="btn btn-sm btn-danger"
                          onClick={() => {
                            if (confirm('Revogar esta chave? Integrações que a usam vão parar.')) {
                              void revoke.run(key.id);
                            }
                          }}
                        >
                          Revogar
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}

/** Log Explorer (PRD §35). Search by request id is the primary path. */
export function ApiLogsPage() {
  const [params, setParams] = useSearchParams();
  const requestId = params.get('requestId') ?? '';
  const statusCode = params.get('statusCode') ?? '';
  const [draft, setDraft] = useState(requestId);

  const logs = useAsync(
    () =>
      logsApi.list({
        requestId: requestId || undefined,
        statusCode: statusCode || undefined,
      }),
    [requestId, statusCode],
  );

  return (
    <Card
      title="Logs da API"
      description="Toda requisição autenticada é registrada com seu request id."
      bodyless
      actions={
        <form
          className="row"
          onSubmit={(event) => {
            event.preventDefault();
            const next = new URLSearchParams(params);
            if (draft) next.set('requestId', draft);
            else next.delete('requestId');
            setParams(next);
          }}
        >
          <input
            aria-label="Request ID"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder="req_01K…"
            style={{ width: 260 }}
          />
          <select
            aria-label="Status"
            value={statusCode}
            onChange={(event) => {
              const next = new URLSearchParams(params);
              if (event.target.value) next.set('statusCode', event.target.value);
              else next.delete('statusCode');
              setParams(next);
            }}
          >
            <option value="">Qualquer status</option>
            <option value="200">200</option>
            <option value="401">401</option>
            <option value="403">403</option>
            <option value="404">404</option>
            <option value="422">422</option>
            <option value="429">429</option>
            <option value="500">500</option>
          </select>
          <button type="submit" className="btn btn-sm">
            Buscar
          </button>
        </form>
      }
    >
      {logs.loading ? (
        <Loading />
      ) : logs.error ? (
        <ErrorState error={logs.error} onRetry={logs.reload} />
      ) : logs.data?.data.length === 0 ? (
        <Empty title="Nenhuma requisição encontrada">
          <p className="muted">Ajuste os filtros ou faça uma chamada à API.</p>
        </Empty>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Data</th>
                <th>Método</th>
                <th>Rota</th>
                <th>Capacidade</th>
                <th>Status</th>
                <th>Duração</th>
                <th>Request ID</th>
              </tr>
            </thead>
            <tbody>
              {(logs.data?.data ?? []).map((log) => (
                <tr key={log.requestId}>
                  <td className="subtle">{formatDateTime(log.createdAt)}</td>
                  <td className="mono">{log.method}</td>
                  <td className="mono">{log.path}</td>
                  <td className="subtle">{log.capability ?? '—'}</td>
                  <td>
                    <span className={`pill pill-${statusCodeTone(log.statusCode)}`}>
                      {log.statusCode}
                    </span>
                    {log.errorCode && <div className="subtle mono">{log.errorCode}</div>}
                  </td>
                  <td className="subtle">{log.durationMs} ms</td>
                  <td>
                    <div className="row">
                      <code className="subtle">{log.requestId.slice(0, 14)}…</code>
                      <CopyButton value={log.requestId} label="Copiar" />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

/** API Explorer (PRD §32) with generated code samples (PRD §31). */
